import Control from "ol/control/Control";
import {
  entryRegistry,
  installTrackHover,
  onEntriesChanged,
  onLiveCursor,
} from "../featureinfo/entryGlue.js";
import {
  clampDrawerHeight,
  loadDrawerState,
  saveDrawerState,
} from "../featureinfo/drawerState.mjs";
import {
  renderDrawerLanes,
  renderDrawerProfile,
} from "../featureinfo/drawerViews.mjs";
import { installPointerTip } from "../featureinfo/pointerTip.js";

function browserStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// The most recently clicked entry; of one click's entries the first line feature in
// sidebar order (the registry keeps insertion order, the sort is stable).
function shownEntry() {
  const entries = entryRegistry.entries();
  if (entries.length === 0) return undefined;
  const latest = Math.max(...entries.map((e) => e.clickedAt));
  // Rows run their selections in interaction order; the drawer follows sidebar order.
  const ofClick = entries
    .filter((e) => e.clickedAt === latest)
    .sort((a, b) =>
      a.element.compareDocumentPosition(b.element) &
      Node.DOCUMENT_POSITION_FOLLOWING
        ? -1
        : 1,
    );
  return (
    ofClick.find((e) => /LineString/.test(e.model.geometryType ?? "")) ??
    ofClick[0]
  );
}

/**
 * Optional bottom drawer for views that need width. Closed by default; only its own
 * handle opens it and only "close" closes it — selecting features never does. Open
 * state, height and tab are kept per browser.
 */
export class DrawerControl extends Control {
  constructor() {
    const element = document.createElement("div");
    element.className = "fe-drawer-root ol-unselectable";
    super({ element });
    this.storage = browserStorage();
    this.state = loadDrawerState(this.storage);

    this.handle = document.createElement("button");
    this.handle.type = "button";
    this.handle.className = "fe-drawer-handle";
    this.handle.textContent = "drawer";

    this.panel = document.createElement("div");
    this.panel.className = "fe-drawer";
    const grip = document.createElement("div");
    grip.className = "fe-drawer-grip";
    grip.title = "Drag to resize";
    const head = document.createElement("div");
    head.className = "fe-drawer-head";
    this.tabs = ["profile", "lanes"].map((tab) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "fe-drawer-tab";
      button.textContent = tab;
      button.addEventListener("click", () => this.update({ tab }));
      return button;
    });
    this.title = document.createElement("span");
    this.title.className = "fe-id";
    this.extra = document.createElement("span");
    this.extra.className = "fe-muted";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "fe-drawer-close";
    close.textContent = "close";
    close.addEventListener("click", () => this.update({ open: false }));
    head.append(...this.tabs, this.title, this.extra, close);
    this.body = document.createElement("div");
    this.body.className = "fe-drawer-body";
    this.panel.append(grip, head, this.body);
    installPointerTip(this.body);
    installTrackHover(this.body, () => shownEntry());
    element.append(this.panel, this.handle);

    this.handle.addEventListener("click", () => this.update({ open: true }));
    grip.addEventListener("mousedown", (event) => this.startResize(event));
    onEntriesChanged(() => this.refresh());
    onLiveCursor((feature, cm) => this.moveCursor(feature, cm));
    this.refresh();
  }

  update(change) {
    this.state = { ...this.state, ...change };
    saveDrawerState(this.storage, this.state);
    this.refresh();
  }

  mapHeight() {
    return (
      this.getMap()?.getTargetElement()?.clientHeight ?? window.innerHeight
    );
  }

  startResize(event) {
    event.preventDefault();
    const y0 = event.clientY;
    const h0 = this.state.heightPx;
    const move = (e) => {
      this.state.heightPx = clampDrawerHeight(
        h0 + (y0 - e.clientY),
        this.mapHeight(),
      );
      this.applyHeight();
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      saveDrawerState(this.storage, this.state);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  }

  // The hover box sits above the open drawer (it reads --fe-drawer-h).
  applyHeight() {
    const height = this.state.open
      ? clampDrawerHeight(this.state.heightPx, this.mapHeight())
      : 0;
    this.panel.style.height = `${height}px`;
    this.getMap()
      ?.getTargetElement()
      ?.style.setProperty("--fe-drawer-h", `${height}px`);
  }

  setMap(map) {
    super.setMap(map);
    this.applyHeight();
  }

  // Not `render`: OL calls a control's render() on every map frame.
  refresh() {
    const { open, tab } = this.state;
    this.panel.style.display = open ? "flex" : "none";
    this.handle.style.display = open ? "none" : "";
    this.applyHeight();
    this.tabs.forEach((b) =>
      b.classList.toggle("fe-on", b.textContent === tab),
    );
    if (!open) return;
    const entry = shownEntry();
    this.shown = entry;
    this.title.textContent = entry ? entry.featureId : "";
    if (!entry) {
      this.extra.textContent = "";
      this.body.replaceChildren(
        Object.assign(document.createElement("div"), {
          className: "fe-muted",
          textContent: "no feature selected",
        }),
      );
      return;
    }
    const length = entry.model.lengthCm;
    this.extra.textContent =
      length === null ? entry.rowName : `${entry.rowName} · ${length} cm`;
    this.body.replaceChildren(
      tab === "lanes"
        ? renderDrawerLanes(document, entry.laneView, length)
        : renderDrawerProfile(document, entry.model),
    );
  }

  // The live cursor, from a map hover or a hover on the sidebar's or drawer's tracks.
  moveCursor(feature, cm) {
    if (!this.state.open) return;
    const plot = this.body.querySelector(".fe-dplot");
    const cursor = plot && plot.querySelector(".fe-cursor");
    if (!cursor) return;
    if (!feature || !this.shown || this.shown.feature !== feature) {
      cursor.style.display = "none";
      return;
    }
    const axis = Number(plot.dataset.axis) || 1;
    cursor.style.display = "";
    cursor.style.left = `${Math.min(Math.max((cm / axis) * 100, 0), 100)}%`;
  }
}
